import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography, Divider, IconButton, Button } from "@mui/material";
import { Trash } from "iconsax-react";

import FundingFinancialsDialog from "../FundingFinancialsDialog";
import {
  SIDEBAR_WIDGET_BORDER_COLOR,
  SIDEBAR_WIDGET_CARD_SX,
  SIDEBAR_WIDGET_TITLE_SX,
} from "./sidebarWidgetStyles";

const BG = "#FFFFFF";
const BORDER = SIDEBAR_WIDGET_BORDER_COLOR;
const TEXT = "#374151";
const MUTED = "#9CA3AF";
const PURPLE = "#9C4FFF";
const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

const TEXT_ACTION_BUTTON_SX = {
  color: PURPLE,
  textTransform: "none",
  fontFamily: "Be Vietnam Pro",
  fontWeight: 700,
  minWidth: 0,
  padding: 0,
  margin: 0,
  "&:hover": {
    backgroundColor: "transparent",
    opacity: 0.9,
  },
};

const DELETE_ACTION_BUTTON_SX = {
  p: 0.5,
  width: 34,
  height: 34,
  borderRadius: 999,
  backgroundColor: "transparent",
  color: PURPLE,
  "&:hover": {
    backgroundColor: "rgba(156, 79, 255, 0.08)",
  },
  "&.Mui-disabled": {
    color: "rgba(156, 79, 255, 0.4)",
  },
};

const normalizeText = (value) => String(value || "").replace(/\s+/g, " ").trim();
const uniqTextValues = (values = []) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => normalizeText(value))
        .filter(Boolean)
    )
  );

function getToken(explicitToken) {
  return (
    explicitToken ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    ""
  );
}

function decodeJwt(token) {
  try {
    const payload = String(token).split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((char) => "%" + ("00" + char.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getCurrentRole() {
  const token = getToken();
  const decoded = decodeJwt(token);
  let role = decoded?.role || localStorage.getItem("role") || "";

  try {
    if (!role) {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        role = parsedUser?.role || "";
      }
    }
  } catch {
    // ignore malformed stored user payloads
  }

  return String(role || "").toLowerCase();
}

async function requestJson({ url, method = "GET", token, body }) {
  const headers = {};
  const authToken = getToken(token);
  const cleanedToken = authToken ? String(authToken).trim().replace(/^Bearer\s+/i, "") : "";

  if (cleanedToken) {
    headers.Authorization = `Bearer ${cleanedToken}`;
    headers["x-access-token"] = cleanedToken;
  }
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(url, {
    method,
    headers,
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const data = await response.json().catch(() => ({}));
  return { response, data };
}

function toFundingState(value = {}) {
  return {
    fundingStage: normalizeText(value.fundingStage ?? value.funding_stage),
    lastFundingRoundAmount: normalizeText(
      value.lastFundingRoundAmount ?? value.last_funding_round_amount ?? value.lastRoundAmount
    ),
    totalRaisedToDate: normalizeText(
      value.totalRaisedToDate ?? value.total_raised_to_date ?? value.totalRaised
    ),
    revenueEstimate: normalizeText(value.revenueEstimate ?? value.revenue_estimate),
  };
}

function Row({ label, value, showTrash = true, onDelete, canManage }) {
  const canDelete = canManage && showTrash && typeof onDelete === "function" && Boolean(normalizeText(value));

  return (
    <Box sx={{ py: 1.65 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Typography
          sx={{
            flex: 1,
            color: MUTED,
            fontSize: 15,
            fontWeight: 600,
            fontFamily: "Be Vietnam Pro",
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            width: 160,
            textAlign: "right",
            color: TEXT,
            fontSize: 15,
            fontWeight: 600,
            fontFamily: "Be Vietnam Pro",
          }}
        >
          {normalizeText(value) || "-"}
        </Typography>

        <Box sx={{ width: 36, display: "flex", justifyContent: "flex-end" }}>
          {canDelete ? (
            <IconButton
              onClick={onDelete}
              size="small"
              sx={DELETE_ACTION_BUTTON_SX}
              aria-label={`Delete ${label}`}
            >
              <Trash size={20} color={PURPLE} variant="Outline" />
            </IconButton>
          ) : (
            <Box sx={{ width: 34, height: 34 }} />
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default function FundingFinancialsCard({
  className,
  sx = {},
  apiBase,
  companyId,
  companyName = "",
  summaryItems = [],
  initialFunding = {},
  onAddNew,
  onEdit,
  onCompanyChange,
}) {
  const normalizedCompanyId = Number(companyId);
  const hasCompanyId = Number.isFinite(normalizedCompanyId) && normalizedCompanyId > 0;
  const initialFundingStage = initialFunding?.fundingStage;
  const initialFundingStageSnake = initialFunding?.funding_stage;
  const initialLastFundingRoundAmount = initialFunding?.lastFundingRoundAmount;
  const initialLastFundingRoundAmountSnake = initialFunding?.last_funding_round_amount;
  const initialLastRoundAmount = initialFunding?.lastRoundAmount;
  const initialTotalRaisedToDate = initialFunding?.totalRaisedToDate;
  const initialTotalRaisedToDateSnake = initialFunding?.total_raised_to_date;
  const initialTotalRaised = initialFunding?.totalRaised;
  const initialRevenueEstimate = initialFunding?.revenueEstimate;
  const initialRevenueEstimateSnake = initialFunding?.revenue_estimate;
  const normalizedInitialFunding = useMemo(
    () =>
      toFundingState({
        fundingStage: initialFundingStage,
        funding_stage: initialFundingStageSnake,
        lastFundingRoundAmount: initialLastFundingRoundAmount,
        last_funding_round_amount: initialLastFundingRoundAmountSnake,
        lastRoundAmount: initialLastRoundAmount,
        totalRaisedToDate: initialTotalRaisedToDate,
        total_raised_to_date: initialTotalRaisedToDateSnake,
        totalRaised: initialTotalRaised,
        revenueEstimate: initialRevenueEstimate,
        revenue_estimate: initialRevenueEstimateSnake,
      }),
    [
      initialFundingStage,
      initialFundingStageSnake,
      initialLastFundingRoundAmount,
      initialLastFundingRoundAmountSnake,
      initialLastRoundAmount,
      initialTotalRaisedToDate,
      initialTotalRaisedToDateSnake,
      initialTotalRaised,
      initialRevenueEstimate,
      initialRevenueEstimateSnake,
    ]
  );
  const normalizedSummaryItems = useMemo(() => {
    return uniqTextValues([companyName, ...summaryItems]);
  }, [companyName, summaryItems]);
  const [funding, setFunding] = useState(() => normalizedInitialFunding);
  const [loading, setLoading] = useState(hasCompanyId);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [dialogState, setDialogState] = useState({ open: false, mode: "add" });
  const role = getCurrentRole();
  const canManageFunding = ["recruiter", "administrator", "admin", "system_user"].includes(role);

  const loadFunding = useCallback(async () => {
    if (!hasCompanyId) {
      setFunding(normalizedInitialFunding);
      setLoading(false);
      setError("");
      return;
    }

    setFunding(normalizedInitialFunding);
    setLoading(true);
    setError("");

    try {
      const base = apiBase || API_BASE || "";
      const url = `${base}/api/companies/${encodeURIComponent(String(normalizedCompanyId))}/info`;
      const { response, data } = await requestJson({ url, method: "GET" });

      if (response.status === 404) {
        setFunding(normalizedInitialFunding);
        setError("");
        return;
      }

      if (!response.ok) {
        throw new Error(data?.error || data?.message || `Failed to load funding details (${response.status})`);
      }

      setFunding(toFundingState(data?.company || {}));
    } catch (fetchError) {
      if (fetchError?.message === "Company not found") {
        setFunding(normalizedInitialFunding);
        setError("");
        return;
      }

      console.error("Failed to load funding and financial details:", fetchError);
      setError(fetchError?.message || "Unable to load funding details.");
    } finally {
      setLoading(false);
    }
  }, [apiBase, hasCompanyId, normalizedCompanyId, normalizedInitialFunding]);

  useEffect(() => {
    loadFunding();
  }, [loadFunding]);

  const updateFunding = useCallback(
    async (nextFunding) => {
      if (!canManageFunding || !hasCompanyId) {
        const nextError = "Unable to save funding details from this view.";
        setError(nextError);
        throw new Error(nextError);
      }

      setProcessing(true);
      setError("");

      try {
        const base = apiBase || API_BASE || "";
        const url = `${base}/api/companies/${encodeURIComponent(String(normalizedCompanyId))}/info`;
        const { response, data } = await requestJson({
          url,
          method: "PUT",
          body: {
            funding_stage: nextFunding?.fundingStage ?? "",
            last_funding_round_amount: nextFunding?.lastFundingRoundAmount ?? "",
            total_raised_to_date: nextFunding?.totalRaisedToDate ?? "",
            revenue_estimate: nextFunding?.revenueEstimate ?? "",
          },
        });

        if (!response.ok) {
          throw new Error(data?.error || data?.message || `Failed to save funding details (${response.status})`);
        }

        const nextCompany = data?.company || null;
        const nextFundingState = toFundingState(nextCompany || nextFunding);
        setFunding(nextFundingState);
        onCompanyChange?.(nextCompany);
      } catch (saveError) {
        console.error("Failed to save funding and financial details:", saveError);
        setError(saveError?.message || "Unable to save funding details.");
        throw saveError;
      } finally {
        setProcessing(false);
      }
    },
    [apiBase, canManageFunding, hasCompanyId, normalizedCompanyId, onCompanyChange]
  );

  const handleOpenDialog = useCallback(
    (mode) => {
      if (!canManageFunding || !hasCompanyId || processing) return;
      setError("");
      if (mode === "edit") {
        onEdit?.();
      } else {
        onAddNew?.();
      }
      setDialogState({ open: true, mode });
    },
    [canManageFunding, hasCompanyId, onAddNew, onEdit, processing]
  );

  const handleCloseDialog = useCallback(() => {
    if (processing) return;
    setDialogState((prev) => ({ ...prev, open: false }));
  }, [processing]);

  const handleClearField = useCallback(
    async (field) => {
      if (!canManageFunding || !hasCompanyId || processing) return;

      const currentValue = normalizeText(funding[field]);
      if (!currentValue) return;

      const labelMap = {
        fundingStage: "funding stage",
        lastFundingRoundAmount: "last funding round amount",
        totalRaisedToDate: "total raised to date",
        revenueEstimate: "revenue estimate",
      };

      if (!window.confirm(`Clear the ${labelMap[field] || "selected funding field"}?`)) {
        return;
      }

      try {
        await updateFunding({
          ...funding,
          [field]: "",
        });
      } catch {
        // The save path already surfaces the error message on the card.
      }
    },
    [canManageFunding, funding, hasCompanyId, processing, updateFunding]
  );

  const dialogTitle =
    dialogState.mode === "edit" ? "Edit Funding & Financials" : "Add Funding & Financials";
  const dialogSubmitLabel =
    dialogState.mode === "edit" ? "Update Funding Details" : "Add Funding Details";
  const dialogResetKey = `${normalizedCompanyId || "new"}:${dialogState.mode}:${JSON.stringify(funding)}`;

  return (
    <>
      <Box
        className={`${className || "widget"}`}
        sx={{
          ...SIDEBAR_WIDGET_CARD_SX,
          background: BG,
          ...sx,
        }}
      >
        <Box sx={{ p: 4 }}>
          <Typography
            className="heading"
            sx={{
              ...SIDEBAR_WIDGET_TITLE_SX,
              mb: 1.6,
            }}
          >
            Funding &amp; Financials
          </Typography>

          {loading ? (
            <Box sx={{ py: 3 }}>
              <Typography sx={{ color: MUTED, fontFamily: "Be Vietnam Pro", fontSize: 13 }}>
                Loading funding details...
              </Typography>
            </Box>
          ) : (
            <>
              <Row
                label="Funding Stage:"
                value={funding.fundingStage}
                showTrash
                canManage={canManageFunding}
                onDelete={() => handleClearField("fundingStage")}
              />
              <Divider sx={{ borderColor: BORDER }} />

              <Row
                label="Last Funding Round Amount:"
                value={funding.lastFundingRoundAmount}
                showTrash
                canManage={canManageFunding}
                onDelete={() => handleClearField("lastFundingRoundAmount")}
              />
              <Divider sx={{ borderColor: BORDER }} />

              <Row
                label="Total Raised to Date:"
                value={funding.totalRaisedToDate}
                showTrash
                canManage={canManageFunding}
                onDelete={() => handleClearField("totalRaisedToDate")}
              />
              <Divider sx={{ borderColor: BORDER }} />

              <Row
                label="Revenue Estimate:"
                value={funding.revenueEstimate}
                showTrash={false}
                canManage={canManageFunding}
              />
            </>
          )}

          {processing ? (
            <Box sx={{ pt: 1.25 }}>
              <Typography sx={{ color: MUTED, fontFamily: "Be Vietnam Pro", fontSize: 13 }}>
                Saving funding details...
              </Typography>
            </Box>
          ) : null}

          {error ? (
            <Typography sx={{ color: "#DC2626", fontFamily: "Be Vietnam Pro", fontSize: 12, mt: 1.5 }}>
              {error}
            </Typography>
          ) : null}

          <Box
            sx={{
              mt: 1.2,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            {canManageFunding ? (
              <>
                <Button
                  sx={{
                    ...TEXT_ACTION_BUTTON_SX,
                    opacity: hasCompanyId ? 1 : 0.5,
                    cursor: hasCompanyId ? "pointer" : "default",
                  }}
                  size="small"
                  onClick={() => handleOpenDialog("add")}
                  disabled={!hasCompanyId || processing}
                >
                  Add New
                </Button>
                <Typography
                  sx={{
                    color: PURPLE,
                    fontFamily: "Be Vietnam Pro",
                    fontSize: 13,
                    fontWeight: 700,
                    userSelect: "none",
                  }}
                >
                  |
                </Typography>
                <Button
                  sx={{
                    ...TEXT_ACTION_BUTTON_SX,
                    opacity: hasCompanyId ? 1 : 0.5,
                    cursor: hasCompanyId ? "pointer" : "default",
                  }}
                  size="small"
                  onClick={() => handleOpenDialog("edit")}
                  disabled={!hasCompanyId || processing}
                >
                  Edit
                </Button>
              </>
            ) : (
              <Typography
                sx={{
                  color: MUTED,
                  fontFamily: "Be Vietnam Pro",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                Only recruiters and administrators can manage funding details.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <FundingFinancialsDialog
        open={dialogState.open}
        onClose={handleCloseDialog}
        onSubmit={updateFunding}
        resetKey={dialogResetKey}
        summaryItems={normalizedSummaryItems}
        initialValues={funding}
        title={dialogTitle}
        submitLabel={dialogSubmitLabel}
      />
    </>
  );
}
